import {Pagination} from "react-bootstrap";
import {useEffect, useState, type ComponentType, type ReactElement, type ReactNode} from "react";

const DefaultContainer = ({ children }: { children: ReactNode }) => <div>{children}</div>;

interface PageableProps<T> {
  items: T[];
  itemsPerPage: number;
  maxPages: number;
  renderItem: (item: T) => ReactNode;
  ItemContainer?: ComponentType<{ children: ReactNode }>;
}

export const Pageable = <T,>(
  { items, itemsPerPage, maxPages, renderItem, ItemContainer = DefaultContainer }: PageableProps<T>
): ReactElement | null => {
  const [ selectedPage, setSelectedPage ] = useState(1)
  const [ shownItems, setShownItems ] = useState<T[]>(items.slice(0, itemsPerPage))
  const pages = Math.ceil(items.length / itemsPerPage)
  const pageItems: ReactElement[] = []

  const onPageChange = (page: number) => {
    if (page < 1 || page > pages) return;
    setSelectedPage(page);
    setShownItems(items.slice((page - 1) * itemsPerPage, page * itemsPerPage))
  }

  // Reset to page 1 when items or itemsPerPage change
  useEffect(() => {
    setSelectedPage(1);
  }, [items, itemsPerPage]);

  // Update shownItems when selectedPage, items, or itemsPerPage change
  useEffect(() => {
    setShownItems(items.slice((selectedPage - 1) * itemsPerPage, selectedPage * itemsPerPage));
  }, [items, itemsPerPage, selectedPage]);

  if (pages <= maxPages) {
    for (let number = 1; number <= pages; number++) {
      pageItems.push(
        <Pagination.Item key={"matches-page-" + number} active={number === selectedPage} onClick={() => onPageChange(number)}>
          {number}
        </Pagination.Item>
      );
    }
  } else {
    pageItems.push(
      <Pagination.First key="first" onClick={() => onPageChange(1)} />
    )
    pageItems.push(
      <Pagination.Prev key="prev" disabled={selectedPage === 1} onClick={() => onPageChange(selectedPage - 1)} />
    )

    const visiblePages = Math.max(maxPages - 4, 1);
    // Calculate start and end page numbers
    let start = Math.max(
      1,
      Math.min(
        selectedPage - Math.floor(visiblePages / 2),
        pages - visiblePages + 1
      )
    );
    let end = Math.min(start + visiblePages - 1, pages);

    // Adjust start if not enough pages at the end
    start = Math.max(1, end - visiblePages + 1);

    // Page numbers
    for (let number = start; number <= end; number++) {
      pageItems.push(
        <Pagination.Item
          key={"matches-page-" + number}
          active={number === selectedPage}
          onClick={() => onPageChange(number)}
        >
          {number}
        </Pagination.Item>
      );
    }

    pageItems.push(
      <Pagination.Next key="next" disabled={selectedPage === pages} onClick={() => onPageChange(selectedPage + 1)} />
    )
    pageItems.push(
      <Pagination.Last key="last" onClick={() => onPageChange(pages)} />
    )
  }

  const paginationBar = pages > 1 ? (
    <div className={"d-flex justify-content-center"}>
      <Pagination>
        {pageItems}
      </Pagination>
    </div>
  ) : null

  return items.length > 0 ? (
    <>
      {paginationBar}
      <ItemContainer>
        {shownItems.map(item => renderItem(item))}
      </ItemContainer>
      {paginationBar}
    </>
  ) : null
}

export default Pageable
